import { Result } from '@/types';
import Decimal from 'decimal.js-light';
import {
  CSSProperties,
  ChangeEventHandler,
  FocusEventHandler,
  HTMLProps,
  KeyboardEventHandler,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { mergeRefs } from 'react-merge-refs';

import { ClientInputError } from '@/lib/errors/client';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { iosSafeInputText } from '@/styles/input';
import { fontStyles } from '@/styles/typography';

import { twMerge } from '@/utils/classnames';

type RootProps = HTMLProps<HTMLSpanElement>;

type InputProps = {
  onChange?: (num: Decimal) => void;
  currentValue?: Decimal;
} & Omit<HTMLProps<HTMLInputElement>, 'onChange' | 'value'>;

const numberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function validateAndFormat(inp: string): Result<Decimal> {
  try {
    const normalizedInput = inp.replaceAll(',', '.');
    const periodCount = (normalizedInput.match(/\./g) || []).length;

    if (normalizedInput.trim().endsWith('.') && periodCount === 1) {
      return {
        type: 'error',
        error: new ClientInputError('unfinished input'),
      };
    }

    if (periodCount > 1) {
      // we remove every period BUT the first one
      // explanation:
      // ┌────────────────────┬─────┬─────────────┬─────────────┬────┐
      // │        (?<=        │ \.  │     .*      │       )     │ \. │
      // │                    │     │             │             │    │
      // │ positive lookbehind│ "." │any character│ end of group│ "."│
      // │                    │     │  (0 or more)│             │    │
      // └────────────────────┴─────┴─────────────┴─────────────┴────┘
      const cleaned = normalizedInput.replace(/(?<=\..*)\./g, '');
      return {
        type: 'success',
        data: new Decimal(numberFormatter.format(Number(cleaned)).replaceAll(',', '')),
      };
    }

    return {
      type: 'success',
      data: new Decimal(numberFormatter.format(Number(normalizedInput)).replaceAll(',', '')),
    };
  } catch (e) {
    return {
      type: 'error',
      error: e instanceof Error ? e : new ClientInputError('unknown error'),
    };
  }
}

type UsdInputContextType = {
  width: number | undefined;
  valueToDisplay: string;
  setValueToDisplay: (s: string) => void;
  currencySymbol: string;
  formatFiatValue: (value: Decimal) => string;
};

const UsdInputContext = createContext<UsdInputContextType>({
  width: undefined,
  valueToDisplay: '',
  setValueToDisplay: () => {},
  currencySymbol: '$',
  formatFiatValue: () => '',
});

const Root = forwardRef<HTMLSpanElement, RootProps>(function Root(
  { className, children, ...props },
  ref,
) {
  const [width, setWidth] = useState<number>(30);
  const [valueToDisplay, setValueToDisplay] = useState('');
  const { currencySymbol, formatter } = useFiatDisplay();

  const formatFiatValue = (value: Decimal) => {
    const formatted = formatter.format(value);
    // Strip the currency symbol and any surrounding whitespace.
    // Using a regex that matches the symbol at the start or end handles
    // both prefix ($100) and suffix (100 kr) symbol placements.
    const symbol = formatter.getCurrencySymbol();
    const escaped = symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return formatted.replace(new RegExp(`^\\s*${escaped}\\s*|\\s*${escaped}\\s*$`, 'g'), '').trim();
  };

  // in order to make the input field automatically grow and shrink depending on its contents,
  // we need to measure the width, which is done using a hidden span
  const hiddenSpanRef = useRef<HTMLSpanElement>(null);

  // useEffect as we need to check AFTER the value of the hidden span changed
  useEffect(() => {
    if (valueToDisplay === '') {
      setWidth(8);
      return;
    }
    setWidth(hiddenSpanRef.current?.offsetWidth || 30);
  }, [valueToDisplay]);

  const wrapperClasses = twMerge(
    fontStyles({
      variant: 'BODY_MD',
      class: [
        'inline-flex items-center px-2 rounded-xs h-fit',
        'bg-transparent transition-colors duration-300 ease-in-out',
        'hover:bg-token-alphalight-200',
        'focus-within:bg-token-alphalight-200',
        'before:hidden after:hidden',
        'min-w-0 overflow-hidden',
        // Must match the inner <input> size so the hidden-span width
        // measurement stays correct.
        iosSafeInputText.md,
      ],
    }),
    className,
  );

  return (
    <span className={wrapperClasses} {...props} ref={ref}>
      <UsdInputContext.Provider
        value={{ width, valueToDisplay, setValueToDisplay, currencySymbol, formatFiatValue }}
      >
        <span ref={hiddenSpanRef} className="absolute invisible">
          {valueToDisplay}
        </span>
        <label className="cursor-text whitespace-nowrap">
          {currencySymbol}
          {children}
        </label>
      </UsdInputContext.Provider>
    </span>
  );
});

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    onBlur,
    onFocus,
    onChange = () => {},
    onKeyUp,
    currentValue = new Decimal(0),
    className,
    style,
    ...rest
  },
  ref,
) {
  const { width, valueToDisplay, setValueToDisplay, formatFiatValue } = useContext(UsdInputContext);
  const [isFocused, setIsFocused] = useState(false);

  const localRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isFocused) {
      setValueToDisplay(formatFiatValue(currentValue));
    }
  }, [setValueToDisplay, isFocused, currentValue, formatFiatValue]);

  const onChangeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
    const result = validateAndFormat(e.currentTarget.value);

    onChange(result.type === 'success' ? result.data : new Decimal(0));
    setValueToDisplay(
      result.type === 'success'
        ? result.data.toString()
        : e.currentTarget.value.replaceAll(/[^.,\d]/g, ''),
    );
  };

  const onFocusHandler: FocusEventHandler<HTMLInputElement> = (e) => {
    // merge onFocus handlers
    if (onFocus) {
      onFocus(e);
    }
    const currentValueFormatted = validateAndFormat(currentValue.toString());
    setValueToDisplay(
      currentValueFormatted.type === 'success' ? currentValueFormatted.data.toString() : '',
    );
    setIsFocused(true);

    setTimeout(() => {
      e.target.select();
    }, 10);
  };

  const onBlurHandler: FocusEventHandler<HTMLInputElement> = (e) => {
    // merge onBlur handlers
    if (onBlur) {
      onBlur(e);
    }

    setIsFocused(false);
  };

  const onKeyUpHandler: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (onKeyUp) {
      onKeyUp(e);
    }
    if (e.key === 'Enter') {
      localRef.current?.blur();
    }
  };

  const inputClasses = twMerge(
    fontStyles({
      variant: 'BODY_MD',
      class: [
        'border-0 h-fit inline-flex bg-transparent text-right',
        'box-content transition-[width] duration-[50ms] ease-linear',
        'outline-none pl-0',
        'before:hidden after:hidden',
        'focus:text-token-gray-1600',
        'selection:bg-[rgba(89,50,243,0.2)]',
        'max-w-full',
        // Must match the wrapper size (above) so the hidden-span width
        // measurement matches the rendered input.
        iosSafeInputText.md,
      ],
    }),
    className,
  );

  return (
    <input
      onChange={onChangeHandler}
      style={{ ...style, '--width': `${width}px`, 'width': `${width}px` } as CSSProperties}
      onFocus={onFocusHandler}
      className={inputClasses}
      onBlur={onBlurHandler}
      value={valueToDisplay}
      onKeyUp={onKeyUpHandler}
      inputMode="decimal"
      maxLength={20}
      {...rest}
      ref={mergeRefs([ref, localRef])}
    />
  );
});

export const UsdInput = { Root, Input };
